import { inject } from 'vue';
import { useMediaStore, type MediaResourceItem } from '@/pinia/stores/media/mediaStore';
import { useMediaListStore } from '@/pinia/stores/media/mediaListStore';
import { useMediaSelectionStore } from '@/pinia/stores/media/mediaSelectionStore';
import { useI18n } from 'vue-i18n';
import EventBus from '@/components/mixins/event-bus-mixin';
import type ApiError from '@/globals/api-error';
import type Utils from '@/globals/utils.js';
import type { AxiosInstance } from 'axios';

/**
 * Composable for media item mutations: rename, tag assignment, pop-id assignment, scheduling, and deletion.
 *
 * Responsibilities:
 * - Delegates raw API calls to `mediaStore` (save / delete)
 * - Keeps the `mediaListStore` in sync after every successful
 *   mutation so the UI reflects changes without a full re-fetch
 * - Keeps `mediaSelectionStore` in sync, replacing stale copies of updated items
 *   and clearing the selection after deletion
 * - Surfaces API errors via `$apiError` and EventBus notifications
 */
export function useMediaUpdate () {
  const $apiError = inject('$apiError') as typeof ApiError;
  const $api = inject('$api') as AxiosInstance;
  const $utils = inject('$utils') as typeof Utils;
  const mediaStore = useMediaStore();
  const mediaListStore = useMediaListStore();
  const mediaSelectionStore = useMediaSelectionStore();
  const { t } = useI18n();

  /**
   * Renames a single media item.
   * @param {MediaResourceItem} mediaItem - Current full media item object
   * @param {string} newName
   * @returns {{ response: MediaResourceItem | null, error: AxiosError }}
   */
  async function renameMedia (mediaItem: MediaResourceItem, newName: string) {
    // Set the optimistic name in the store immediately so the UI reflects the change before the API round-trip completes.
    mediaStore.setOptimisticName(mediaItem.resourceId, newName);

    const requestData = buildRequestData(mediaItem, { name: newName });
    const { response, error } = await mediaStore.update(mediaItem.resourceId, requestData);

    if (!error) {
      syncUpdated(response.data);
    } else {
      notifyError(error);
    }

    mediaStore.clearOptimisticName(mediaItem.resourceId);
    return { response, error };
  }

  /**
   * Assigns a pop identifier to a single media item.
   * @param {MediaResourceItem} mediaItem - Current full media item object
   * @param {string} popId
   * @param {Object} [options]
   * @returns {{ response: MediaResourceItem | null, error: AxiosError }}
   */
  async function updateMediaPopId (mediaItem: MediaResourceItem, popId: string) {
    const requestData = buildRequestData(mediaItem, { popId });
    const { response, error } = await mediaStore.update(mediaItem.resourceId, requestData);

    if (!error) {
      syncUpdated(response.data);
    } else {
      notifyError(error);
    }

    return { response, error };
  }

  /**
   * Replaces the tags on media item.
   * @param {MediaResourceItem} mediaItem - Current full media item object
   * @param {string[]} tags - Complete replacement tag array
   * @param {Object} [options]
   * @returns {{ response: MediaResourceItem | null, error: AxiosError }}
   */
  async function updateMediaTags (mediaItem: MediaResourceItem, tags: string[]) {
    const requestData = buildRequestData(mediaItem, { tags });
    const { response, error } = await mediaStore.update(mediaItem.resourceId, requestData);

    if (!error) {
      syncUpdated(response.data);
    } else {
      notifyError(error);
    }

    return { response, error };
  }

  /**
   * Shows a confirmation dialog to force delete media items.
   * This is used when media items are blocked from deletion
   * because they are still in use in a project.
   * @param {MediaResourceItem[]} blocked - The media items to force delete
   * @param {Object} [options] - Additional options for deletion
   * @param {string} [options.mediaOrientation] - The orientation of the media (used for the force-delete confirmation dialog)
   * @return {Promise<boolean>} - Resolves to true if the user confirms, false if they cancel
   */
  function confirmForceDeleteItems (blocked: MediaResourceItem[], options?: { mediaOrientation?: string }) {
    const deleteCount = blocked.length;
    const isMultipleDelete = deleteCount > 1;
    const descriptionI18n = isMultipleDelete
      ? {
          key: 'media.multipleMediaDeleteConfirmation',
          params: { mediaCount: deleteCount }
        }
      : {
          key: 'media.mediaDeleteConfirmation',
          params: { mediaName: blocked[0].name }
        };
    return new Promise((resolve) => {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'warning',
        title: t('confirm.deleteMedia'),
        descriptionI18n,
        alert: {
          message: isMultipleDelete ? t('media.multipleMediaDeleteWarning') : t('media.mediaDeleteWarning')
        },
        preview: {
          items: blocked,
          mediaOrientation: options?.mediaOrientation
        },
        confirmButtonText: t('buttons.deleteMedia'),
        confirmButtonIcon: 'flowbite:trash-bin-solid',
        callbacks: {
          eventOnClose: () => resolve(false),
          eventOKButtonClick: () => resolve(true)
        }
      });
    });
  }

  /**
   * Shows a confirmation dialog to delete media items.
   * @param {MediaResourceItem[]} items - The media items to delete
   * @return {Promise<boolean>} - Resolves to true if the user confirms, false if they cancel
   */
  function confirmDeleteItems (items: MediaResourceItem[]) {
    const deleteCount = items.length;
    const isMultipleDelete = deleteCount > 1;

    const descriptionI18n = isMultipleDelete
      ? {
          key: 'media.multipleMediaDeleteConfirmation',
          params: { mediaCount: deleteCount }
        }
      : {
          key: 'media.mediaDeleteConfirmation',
          params: { mediaName: items[0].name }
        };

    const alert = {
      message: isMultipleDelete ? t('media.multipleMediaDeleteWarning') : t('media.mediaDeleteWarning')
    };

    return new Promise((resolve) => {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'warning',
        title: t('confirm.deleteMedia'),
        descriptionI18n,
        alert,
        confirmButtonText: t('buttons.deleteMedia'),
        confirmButtonIcon: 'flowbite:trash-bin-solid',
        callbacks: {
          eventOnClose: () => resolve(false),
          eventOKButtonClick: () => resolve(true)
        }
      });
    });
  }

  /**
   * Duplicates a project-type media item (svg/html).
   * Flow: GET /project/:id → POST /project (copy) → GET /media/:newId → prepend to list.
   * Only valid for items whose preview.type is 'svg' or 'html'.
   *
   * @param {MediaResourceItem} mediaItem - The media item to duplicate
   * @returns {{ error: AxiosError | null }}
   */
  async function copyMedia (mediaItem: MediaResourceItem) {
    try {
      // 1. Fetch full project definition
      const projectResponse = await $api.get(
        `${CONFIG.API_BASE_URL}/project/${mediaItem.resourceId}`,
        { params: { use_qsa: 1 } }
      );

      // 2. POST a copy of the project
      const projectData = $utils.filterProjectDataForBackend({ project: projectResponse.data });
      const createResponse = await $api.post(
        `${CONFIG.API_BASE_URL}/project`,
        projectData
      );

      // 3. Fetch the new media item's details and prepend it to the list
      const newResourceId = createResponse.data.resourceId;
      const { response, error: fetchError } = await mediaStore.fetchDetails(newResourceId);
      if (fetchError) {
        notifyError(fetchError);
        return { error: fetchError };
      }

      mediaListStore.prependItem(response.data);
      return { error: null };
    } catch (error) {
      notifyError(error);
      return { error };
    }
  }

  /**
   * Deletes one or more media items.
   * Items blocked with 409/412 (still in use in a project) are returned so the
   * caller can ask the user to confirm and then call `forceDeleteMedia`.
   *
   * @param {MediaResourceItem[]} mediaItems
   * @param {Object} [options]
   * @param {boolean} [options.isAlert=false]
   * @returns {{ blocked: MediaResourceItem[], failed: MediaResourceItem[], succeeded: MediaResourceItem[] }}
   */
  async function deleteMedia (mediaItems: MediaResourceItem[], options: { isAlert?: boolean } = {}) {
    const blocked: MediaResourceItem[] = [];
    const failed: MediaResourceItem[] = [];
    const succeeded: MediaResourceItem[] = [];

    await Promise.all(
      mediaItems.map(async (item) => {
        const { error } = await mediaStore.delete(item.resourceId, options);

        if (!error) {
          succeeded.push(item);
          // Only unselect item if deletion was successful
          // If deletion failed, we want the item to remain
          // selected so user can retry.
          mediaSelectionStore.removeItem(item);
          mediaListStore.removeItem(item.resourceId);
        } else {
          const status = error?.response?.status;
          if (status === 409 || status === 412) {
            blocked.push(item);
          } else {
            failed.push(item);
          }
        }
      })
    );

    // Show only 1 error notification if multiple deletions failed.
    // TODO: Add better error handling.
    if (failed.length) {
      notifyError();
    }

    return { blocked, failed, succeeded };
  }

  /**
   * Force-deletes one or more media items, bypassing the in-use check.
   * Only call this after the user has explicitly confirmed the force-delete.
   *
   * @param {MediaResourceItem[]} mediaItems
   * @param {Object} [options]
   * @param {boolean} [options.isAlert=false]
   */
  async function forceDeleteMedia (mediaItems: MediaResourceItem[], options: { isAlert?: boolean } = {}) {
    const failed: MediaResourceItem[] = [];
    const succeeded: MediaResourceItem[] = [];
    await Promise.all(
      mediaItems.map(async (item) => {
        const { error } = await mediaStore.delete(item.resourceId, { ...options, force: true });

        if (!error) {
          succeeded.push(item);
          // Only unselect item if deletion was successful
          // If deletion failed, we want the item to remain
          // selected so user can retry.
          mediaSelectionStore.removeItem(item);
          mediaListStore.removeItem(item.resourceId);
        } else {
          failed.push(item);
        }
      })
    );

    // Show only 1 error notification if multiple deletions failed.
    // TODO: Add better error handling.
    if (failed.length) {
      notifyError();
    }

    return { failed, succeeded };
  }

  /**
   * Assembles the full PUT request body for a media item.
   *
   * The PUT `/media/:id` endpoint is not a PATCH — the server requires all root-level
   * fields to be present. This function copies the item's current values and merges in
   * any `overrides`, giving callers a safe way to change one field without accidentally
   * clearing others.
   *
   * @param {Object} mediaItem - Current complete media item
   * @param {Object} [overrides] - Fields to override (e.g. `{ name: 'New name' }`)
   * @returns {Object} Full request payload
   */
  function buildRequestData (mediaItem: MediaResourceItem, overrides: Partial<MediaResourceItem> = {}) {
    // NOTE: Exclude `schedules` from the base request payload
    // for users with no schedule permissions. Including it in
    // the request will cause a 403 error. This way, it will
    // only be included from `overrides` if the UI allows it.
    const base: Partial<MediaResourceItem> = {
      name: mediaItem.name,
      type: mediaItem.type ?? undefined,
      tags: mediaItem.tags ?? [],
      validity: mediaItem.validity ?? {},
      popId: mediaItem.popId ?? undefined,
    };

    if (mediaItem.data) base.data = mediaItem.data;
    if (mediaItem.alert) base.alert = mediaItem.alert;
    if (mediaItem.dur != null) base.dur = mediaItem.dur;

    return { ...base, ...overrides } as MediaResourceItem;
  }

  /**
   * Syncs an updated item into mediaListStore and the current selection.
   */
  function syncUpdated (updatedItem: MediaResourceItem) {
    mediaListStore.stageMediaUpdate(updatedItem);

    const inSelection = mediaSelectionStore.selection.some(
      s => s.resourceId === updatedItem.resourceId
    );

    if (inSelection) {
      mediaSelectionStore.setSelection(
        mediaSelectionStore.selection.map(s =>
          s.resourceId === updatedItem.resourceId ? updatedItem : s
        )
      );
    }
  }

  function notifyError (err?: any) {
    if (err?.response?.status) {
      $apiError.handleAPIRequestError(err, { showDialogNotification: true });
    } else {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message: t('playerSetup.UnknownError')
        },
        callbacks: { eventOnClose: () => {} },
      });
    }
  }

  return {
    renameMedia,
    buildRequestData,
    syncUpdated,
    updateMediaTags,
    updateMediaPopId,
    copyMedia,
    deleteMedia,
    forceDeleteMedia,
    confirmForceDeleteItems,
    confirmDeleteItems
  };
}
